/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file TeamChatSocket.js
 * @description
 * Socket.IO transport of the Team Chat module. Clones the
 * {@link module:helpers/SupportChatSocket} / MailContext pattern (same client,
 * Bearer `auth`, `join:user` personal room re-joined on every reconnect,
 * Demo-mode PII redaction) but is IMPERATIVE: the single owner is
 * `TeamChatProvider`, which opens exactly one connection per signed-in user +
 * store and tears it down on store switch / logout.
 *
 * Server → client events land in the user's own `user:<id>` room. Payloads are
 * accepted both bare (`emitToUserRoom`) and in the realtime envelope
 * `{event, payload, emittedAt}`.
 */

import {io as createSocketClient} from 'socket.io-client';
import {ApiConfig} from '../config/ApiConfig';
import {getAccessToken} from './storage';
import {redact} from '../demo';

/** Every chat event the server pushes to `user:<id>`. */
export const TEAM_CHAT_EVENTS = [
    'chat:message',
    'chat:message:updated',
    'chat:message:deleted',
    'chat:channel:updated',
    'chat:channel:removed',
    'chat:read',
    'chat:typing',
    'chat:meeting',
    // Guest knocking on a meeting I am in: {meetingId, guest} (admit / deny toast).
    'chat:meeting:lobby',
    // Ephemeral app / slash-command reply, only for me, never persisted: {channelId, threadRootId, bot, botAvatar, app, text, blocks, ephemeral, createdAt}.
    'chat:ephemeral',
];

/**
 * Unwrap the realtime envelope when present, else return the bare payload.
 * @param {*} data Raw socket payload.
 * @returns {Object} Event payload (always an object).
 */
export const unwrapTeamChatPayload = (data) => {
    if (data && typeof data === 'object' && data.payload && typeof data.payload === 'object'
        && ('event' in data || 'emittedAt' in data)) {
        return data.payload;
    }

    return data && typeof data === 'object' ? data : {};
};

/**
 * Open the chat socket.
 *
 * @param {object} params
 * @param {string} params.userId Signed-in user id (personal room).
 * @param {(eventName: string, payload: Object) => void} params.onEvent Event sink.
 * @param {(connected: boolean, info: {reconnect: boolean}) => void} [params.onStatus] Connection sink.
 * @returns {{emit: (eventName: string, payload: Object) => boolean, close: () => void}}
 */
export function connectTeamChatSocket({userId, onEvent, onStatus}) {
    let socket = null;
    let closed = false;
    let connectedOnce = false;

    const notifyStatus = (connected, reconnect = false) => {
        try {
            onStatus?.(connected, {reconnect});
        } catch (_) {
            // never let a sink crash the socket
        }
    };

    (async () => {
        let token = null;
        try {
            token = await getAccessToken();
        } catch (_) {
            token = null;
        }
        if (closed || !userId) {
            return;
        }
        try {
            socket = createSocketClient(ApiConfig.apiURL, {
                transports: ['websocket', 'polling'],
                timeout: 5000,
                reconnection: true,
                reconnectionAttempts: Infinity,
                reconnectionDelay: 3000,
                auth: token ? {token, userId} : {userId},
            });

            socket.on('connect', () => {
                if (closed) {
                    return;
                }
                // (Re)join the personal room on every (re)connect.
                socket.emit('join:user', userId);
                notifyStatus(true, connectedOnce);
                connectedOnce = true;
            });
            socket.on('disconnect', () => {
                if (!closed) {
                    notifyStatus(false);
                }
            });
            socket.on('connect_error', () => {
                if (!closed) {
                    notifyStatus(false);
                }
            });

            TEAM_CHAT_EVENTS.forEach((eventName) => {
                socket.on(eventName, (data) => {
                    if (closed) {
                        return;
                    }
                    // Socket.IO bypasses the fetch/axios Demo proxy — redact here too.
                    const payload = redact(unwrapTeamChatPayload(data)) || {};
                    try {
                        onEvent?.(eventName, payload);
                    } catch (_) {
                        // Best-effort; a handler bug must not kill the socket.
                    }
                });
            });
        } catch (_) {
            socket = null;
            notifyStatus(false);
        }
    })();

    return {
        emit(eventName, payload) {
            if (!socket || !socket.connected) {
                return false;
            }
            try {
                socket.emit(eventName, payload);

                return true;
            } catch (_) {
                return false;
            }
        },
        close() {
            closed = true;
            if (socket) {
                try {
                    socket.removeAllListeners();
                    socket.disconnect();
                } catch (_) {
                    // ignore teardown races
                }
            }
            socket = null;
        },
    };
}

export default connectTeamChatSocket;
